

import { useEffect, useState } from "react";
import { ListOrdered } from "lucide-react";
import { Alert, Button, Drawer, KeyValues, StatusDot, Tag, type DotTone , LoadingState } from "@/shared/rd";
import { Eyebrow, Meta } from "../rd-local";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import type {
  StoreChargeStatus,
  StoreSubscriptionLedgerResponse,
} from "@/lib/api/types";
import {
  STORE_SUBSCRIPTION_ALLOCATION_KIND,
  STORE_CHARGE_SOURCE,
  STORE_CHARGE_STATUS,
} from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionLedgerQuery } from "@/shared/stores/services/queries/useStoreSubscriptionLedgerQuery";

type Props = {
  storeId: string;
  locale: string;
  triggerVariant?: "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function StoreScheduleLedgerModal({
  storeId,
  locale,
  triggerVariant = "icon",
  triggerClassName,
  triggerLabel,
  onOpen,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const { data, isLoading, error: ledgerError, refetch } =
    useStoreSubscriptionLedgerQuery({
      storeId: isOpen ? storeId : "",
    });

  // A raw status enum is not readable copy — every charge state gets an Arabic
  // label and a colour, and never colour alone.
  const chargeStatusLabels: Record<StoreChargeStatus, string> = {
    [STORE_CHARGE_STATUS.PENDING]: t(keys.admin.subscriptionChargeStatusPending),
    [STORE_CHARGE_STATUS.PARTIAL]: t(keys.admin.subscriptionChargeStatusPartial),
    [STORE_CHARGE_STATUS.PAID]: t(keys.admin.subscriptionChargeStatusPaid),
    [STORE_CHARGE_STATUS.VOID]: t(keys.admin.subscriptionChargeStatusVoid),
    [STORE_CHARGE_STATUS.WAIVED]: t(keys.admin.subscriptionChargeStatusWaived),
  };

  const chargeStatusTones: Record<StoreChargeStatus, DotTone> = {
    [STORE_CHARGE_STATUS.PENDING]: "blue",
    [STORE_CHARGE_STATUS.PARTIAL]: "amber",
    [STORE_CHARGE_STATUS.PAID]: "green",
    // Void and waived both mean "nothing is owed any more", so they read as
    // closed-and-inert rather than as a failure.
    [STORE_CHARGE_STATUS.VOID]: "grey",
    [STORE_CHARGE_STATUS.WAIVED]: "grey",
  };

  const formatDate = (value: string | null) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  };

  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        // Order charges are small and often fractional; rounding them away
        // would make the ledger stop adding up.
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      void refetch();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, refetch]);

  const error =
    ledgerError instanceof ApiJsonClientError
      ? ledgerError.message || t(keys.errors.generic)
      : null;
  const ledger: StoreSubscriptionLedgerResponse | null = data ?? null;

  const openModal = () => {
    setOpen(true);
    onOpen?.();
  };

  const label = triggerLabel ?? t(keys.admin.storeSubscriptionsScheduleLedgerAction);
  const chip = (key: string, text: string) => (
    <span
      key={key}
      className="rd-num"
      style={{ padding: "2px 8px", borderRadius: "var(--rd-radius-chip)", background: "var(--rd-surface-sunken)", fontSize: 12, color: "var(--rd-text-secondary)" }}
    >
      {text}
    </span>
  );

  return (
    <>
      {hideTrigger ? null : triggerVariant === "menu" ? (
        <Button variant="ghost" size="sm" className={triggerClassName} onClick={openModal}>
          {label}
        </Button>
      ) : (
        <Button variant="ghost" iconOnly icon={ListOrdered} title={label} className={triggerClassName} onClick={openModal} />
      )}

      <Drawer
        open={isOpen}
        onClose={() => setOpen(false)}
        closeLabel={t(keys.common.close)}
        badge="bill"
        title={t(keys.admin.storeSubscriptionsScheduleLedgerTitle)}
        sub={t(keys.admin.storeSubscriptionsScheduleLedgerHint)}
        width={560}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {error ? (
            <Alert tone="error">
              {error}
              <span style={{ display: "block", marginTop: 2 }}>{t(keys.errors.recovery)}</span>
            </Alert>
          ) : null}
          {isLoading ? (
            <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.ledgerStatePage") })} sub={t(keys.shell.loadingSub)} rows={0} />
          ) : ledger ? (
            <>
              <section className="rd-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                <span style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>{t(keys.admin.storeSubscriptionsWalletBalance)}</span>
                <span className="rd-num" style={{ fontSize: 20, fontWeight: 700 }}>
                  {formatAmount(ledger.creditBalance, ledger.currency)}
                </span>
                {ledger.creditAllocations.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {ledger.creditAllocations.map((allocation) =>
                      chip(
                        allocation.id,
                        `${formatAmount(allocation.remainingAmount, ledger.currency)} · ${formatDate(allocation.paymentPaidAt)}`,
                      ),
                    )}
                  </div>
                ) : null}
              </section>

              {ledger.schedules.length === 0 ? <Meta>{t(keys.admin.subscriptionChargesEmpty)}</Meta> : null}
              {ledger.schedules.map((item) => {
                const remaining = Math.max(item.schedule.amountDue - item.schedule.paidAmount, 0);
                const isOrderCharge = item.schedule.source === STORE_CHARGE_SOURCE.ORDER;
                const allocations = item.allocations.filter(
                  (allocation) => allocation.kind === STORE_SUBSCRIPTION_ALLOCATION_KIND.SCHEDULE,
                );
                return (
                  <section
                    key={item.schedule.id}
                    className="rd-card"
                    style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Tag tone={isOrderCharge ? "blue" : "grey"}>
                        {isOrderCharge
                          ? t(keys.admin.subscriptionChargeSourceOrder)
                          : t(keys.admin.subscriptionChargeSourcePeriod)}
                      </Tag>
                      <span style={{ fontSize: 13, color: "var(--rd-text-secondary)" }}>{formatDate(item.schedule.dueDate)}</span>
                      <span style={{ flex: 1 }} />
                      <StatusDot tone={chargeStatusTones[item.schedule.status] ?? "grey"}>
                        {chargeStatusLabels[item.schedule.status] ?? item.schedule.status}
                      </StatusDot>
                    </div>
                    {/* The human order code, never the uuid: a truncated
                        `sourceRefId` matches nothing an operator can search.
                        Null on a billing period or a deleted order, in which
                        case the line is simply absent. */}
                    {isOrderCharge && item.schedule.orderCode ? (
                      <Meta>
                        {t(keys.admin.subscriptionChargeOrderRef)}:{" "}
                        <span dir="ltr" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {item.schedule.orderCode}
                        </span>
                      </Meta>
                    ) : null}
                    <KeyValues
                      rows={[
                        {
                          label: t(keys.admin.subscriptionScheduleDueAmount),
                          value: <span className="rd-num">{formatAmount(item.schedule.amountDue, item.schedule.currency)}</span>,
                        },
                        {
                          label: t(keys.admin.subscriptionSchedulePaid),
                          value: <span className="rd-num">{formatAmount(item.schedule.paidAmount, item.schedule.currency)}</span>,
                        },
                        {
                          label: t(keys.admin.subscriptionScheduleRemaining),
                          value: (
                            <span className="rd-num" style={{ fontWeight: 600 }}>
                              {formatAmount(remaining, item.schedule.currency)}
                            </span>
                          ),
                        },
                      ]}
                    />
                    {allocations.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <Eyebrow>{t(keys.admin.subscriptionSchedulePaid)}</Eyebrow>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {allocations.map((allocation) =>
                            chip(
                              allocation.id,
                              `${formatAmount(allocation.amount, item.schedule.currency)} · ${formatDate(allocation.paymentPaidAt)}`,
                            ),
                          )}
                        </div>
                      </div>
                    ) : item.allocations.length === 0 ? (
                      <Meta>{t(keys.admin.subscriptionChargeAllocationsEmpty)}</Meta>
                    ) : null}
                  </section>
                );
              })}
            </>
          ) : null}
        </div>
      </Drawer>
    </>
  );
}
